import { isPlatformBrowser, Location } from '@angular/common';
import { isDevMode, NgZone, OnDestroy, effect, inject, PLATFORM_ID, SecurityContext, TransferState, makeStateKey, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { shouldLog } from './utils/logging';
import { Component, Optional } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { FooterComponent } from './footer.component';
import { HOST, SID } from './interfaces';
import { AuctionService, AuthService, NotificationService, PathService, PodiumVisibilityService, SettingsService, TaxExemptDialogService, UserService } from './services';
import { LoadingService } from './services/loading.service';
import { TrackEventService } from './services/track-event.service';
import { TopbarComponent } from './ui/topbar.component';
import { ClarityProject, Event, GTM, TrackEvent } from './enums';
import { ToastModule } from 'primeng/toast';


declare const window: any;
declare const ga: any;

declare var fbq: any;

const STAPE_SCRIPT_URL = 'https://sst.rasmus.com/9okcrvzsg.js?';
const STAPE_QUERY_PARAM = '1j=DhdHMSYuRiRQRzQ6KFclSRFOSFhDRQ8BWgEbBR8WGEEEHBc%3D';
const STAPE_SCRIPT_ELEMENT_ID = 'stape-gtm-loader';
const BRAZE_SCRIPT_ID = 'braze-web-sdk';
const BRAZE_SCRIPT_SRC = 'https://js.appboycdn.com/web-sdk/latest/braze.min.js';
const PODIUM_HIDE_STYLE_ID = 'podium-widget-visibility-override';

export interface IHTML {
  header: string;
  footer: string;
}

@Component({
    selector: 'app-root',
    imports: [
    RouterOutlet,
    FooterComponent,
    TopbarComponent,
    ToastModule
],
    providers: [UserService, PathService, SettingsService, AuctionService],
    template: `
  <p-toast position="bottom-right" [styleClass]="notificationService.getDefaultToastStackClass()" />
  <p-toast
    key="tax-exempt-document"
    position="bottom-right"
    [styleClass]="taxExemptToastStyleClass"
  >
    <ng-template let-message pTemplate="message">
      <div class="tax-exempt-document-toast__content" role="status" aria-live="polite">
        <div class="tax-exempt-document-toast__main">
          <div class="tax-exempt-document-toast__copy">
            <p class="tax-exempt-document-toast__title">{{ message.summary }}</p>
            <p class="tax-exempt-document-toast__detail">{{ message.detail }}</p>
          </div>
          <button type="button" class="tax-exempt-document-toast__upload" (click)="openTaxExemptUpload()">
            Upload
          </button>
        </div>
        <button
          type="button"
          class="tax-exempt-document-toast__close"
          aria-label="Dismiss tax exempt document reminder"
          (click)="dismissTaxExemptReminder()"
        >
          <i class="pi pi-times" aria-hidden="true"></i>
        </button>
      </div>
    </ng-template>
  </p-toast>
  <div class="app-shell">
    <div id="gtm-noscript"></div>
    <app-topbar-component class="no-print"></app-topbar-component>
    <main class="app-main">
      <router-outlet></router-outlet>
    </main>
  </div>
  <app-footer [footer]="footer" [sid]="sid"></app-footer>
  @if (shouldLoadRasmusFonts) {
    <div>
      <link
        crossorigin="anonymous"
        rel="stylesheet"
        id="jevelin-fonts-css"
        href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&amp;family=Raleway:wght@400;700&amp;display=swap"
        type="text/css"
        media="all"
        />
    </div>
  }`,
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnDestroy {
  public footer: any;
  public sid = '';
  /** SIDs that should load the Rasmus font bundle. */
  private readonly rasmusFontSids = ['rasmus_auctions_appspot_com', 'superb_ascent_appspot_com'];

  public activatedRoute = inject(ActivatedRoute);
  private loadingService = inject(LoadingService);
  private trackEventService = inject(TrackEventService);
  private domSanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private location = inject(Location);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  public notificationService = inject(NotificationService);
  private settingsService = inject(SettingsService);
  private podiumVisibilityService = inject(PodiumVisibilityService);
  private taxExemptDialogService = inject(TaxExemptDialogService);
  private ts = inject(TransferState);
  private ngZone = inject(NgZone);
  private injectedSid = inject(SID, { optional: true });
  private host = inject(HOST, { optional: true });
  private platformId = inject(PLATFORM_ID);
  private readonly routerEvent = toSignal(this.router.events, { initialValue: null });
  private readonly currentPath = signal('');
  private unregisterLocationChange?: VoidFunction;
  private brazePromise: Promise<any> | null = null;
  private taxExemptReminderChecked = false;
  private taxExemptReminderCheckInFlight = false;
  private taxExemptReminderRetry?: ReturnType<typeof setTimeout>;
  private conversionTracked = false;
  private readonly facebookPixelEffect = effect(() => {
    const event = this.routerEvent();
    if (!isPlatformBrowser(this.platformId) || !(event instanceof NavigationEnd)) {
      return;
    }
    if (typeof fbq === 'function') {
      fbq('track', 'PageView');
    }
  });
  private readonly conversionEffect = effect(() => {
    const event = this.routerEvent();
    if (!isPlatformBrowser(this.platformId) || this.conversionTracked || !(event instanceof NavigationEnd)) {
      return;
    }
    const params = this.activatedRoute.snapshot.queryParams;
    const aid = this.activatedRoute.snapshot.params?.aid;
    if (!aid) {
      return;
    }

    this.trackEventService.pushEvent(Event.INTERACTION_STARTS, aid, params);
    this.conversionTracked = true;
  });
  private readonly taxExemptReminderAuthEffect = effect(() => {
    if (!this.authService.resolved()) {
      return;
    }
    if (this.authService.isAuthenticated()) {
      void this.showTaxExemptReminderIfNeeded();
      return;
    }

    this.taxExemptReminderChecked = false;
    this.cancelTaxExemptReminderRetry();
    this.notificationService.clearAuthBoundReminders();
  });
  private readonly podiumVisibilityEffect = effect(() => {
    const currentPath = this.currentPath();
    const maxBidDialogOpen = this.podiumVisibilityService.maxBidDialogOpen();
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.updatePodiumVisibility(this.isProductsRoute(currentPath) || maxBidDialogOpen);
  });

  constructor() {
    const fn = '[app.constructor]';
    const SITE_KEY = makeStateKey<string>('Site');
    let sid = '';

    if (isPlatformBrowser(this.platformId) && this.ts.hasKey(SITE_KEY)) {
      sid = this.ts.get<string>(SITE_KEY, null!);
    } else if (this.injectedSid) {
      if (shouldLog()) {
        console.log(`${fn} Saving injected to TransferState: ${this.injectedSid}`);
      }
      this.ts.set(SITE_KEY, this.injectedSid);
      sid = this.injectedSid;
    }
    if (shouldLog()) {
      console.log(`${fn} sid: ${sid}`);
    }
    this.sid = sid;

    if (isPlatformBrowser(this.platformId)) {
      this.currentPath.set(this.getCurrentBrowserPath());
      this.unregisterLocationChange = this.location.onUrlChange((url) => {
        this.currentPath.set(this.extractPath(url));
      });
      this.scheduleBrowserResources(sid);
    }
  }

  /**
   * Defers brand marketing/analytics scripts (GTM/Stape, FB Pixel, Podium,
   * Clarity, Braze) until the first user interaction and the browser is idle,
   * so they never compete with hydration, the LCP image, or first paint for
   * network/main-thread time. A 45 s fallback still counts fully passive
   * sessions (kept deliberately long: pages with live Firestore listeners
   * hold the network open for ~30 s, and the batch must stay out of that
   * window). Runs outside Angular so none of the third-party
   * timers/listeners trigger change detection.
   */
  private scheduleBrowserResources(sid: string): void {
    // Only called from the isPlatformBrowser branch, so globalThis is window.
    const win = globalThis as unknown as Window & {
      requestIdleCallback?: (cb: () => void, options?: { timeout?: number }) => number;
    };
    const start = () => void this.loadBrowser(sid);
    const whenIdle = () => {
      if (typeof win.requestIdleCallback === 'function') {
        win.requestIdleCallback(start, { timeout: 3000 });
      } else {
        setTimeout(start, 1500);
      }
    };
    this.ngZone.runOutsideAngular(() => {
      this.runOnFirstInteraction(whenIdle, 45000);
    });
  }

  /**
   * Runs a loader on the first user interaction — pointer, touch, key, or
   * wheel, deliberately NOT `scroll`: browsers fire synthetic scroll events
   * during load (scroll restoration, anchor jumps), which would defeat the
   * gate. Pass `fallbackMs` to also run after a delay for visitors who never
   * interact; omit it for widgets that are only useful to an interacting user
   * (e.g. the Podium chat bubble).
   */
  private runOnFirstInteraction(load: () => void, fallbackMs?: number): void {
    let done = false;
    const events: (keyof WindowEventMap)[] = ['pointerdown', 'touchstart', 'keydown', 'wheel'];
    const run = () => {
      if (done) {
        return;
      }
      done = true;
      events.forEach((event) => window.removeEventListener(event, run));
      if (fallbackTimer !== undefined) {
        clearTimeout(fallbackTimer);
      }
      load();
    };
    events.forEach((event) => window.addEventListener(event, run, { passive: true, once: true }));
    const fallbackTimer = fallbackMs === undefined ? undefined : setTimeout(run, fallbackMs);
  }

  /**
   * Determines whether the current site should load the Rasmus font bundle.
   */
  get shouldLoadRasmusFonts(): boolean {
    return this.rasmusFontSids.includes(this.sid);
  }

  get taxExemptToastStyleClass(): string {
    return [
      'tax-exempt-document-toast',
      this.notificationService.getReminderStackClass('tax-exempt-document'),
      this.hasVisibleStickyBidModule() ? 'tax-exempt-document-toast--sticky-offset' : '',
    ].filter(Boolean).join(' ');
  }

  ngOnDestroy(): void {
    this.cancelTaxExemptReminderRetry();
    this.unregisterLocationChange?.();
    this.updatePodiumVisibility(false);
  }

  public openTaxExemptUpload(): void {
    this.taxExemptDialogService.open(this.findCurrentAuctionId());
  }

  public dismissTaxExemptReminder(): void {
    this.notificationService.clearTaxExemptDocumentReminder();
  }

  public hasVisibleStickyBidModule(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const stickyModule = globalThis.document?.querySelector<HTMLElement>('app-sticky-bid-module .sticky-bid-module');
    if (!stickyModule) {
      return false;
    }

    const rect = stickyModule.getBoundingClientRect();
    const styles = globalThis.getComputedStyle(stickyModule);
    return rect.height > 0 && rect.width > 0 && styles.display !== 'none' && styles.visibility !== 'hidden';
  }

  private async showTaxExemptReminderIfNeeded(retryCount = 0): Promise<void> {
    if (
      this.taxExemptReminderChecked ||
      this.taxExemptReminderCheckInFlight ||
      !this.authService.isAuthenticated() ||
      !isPlatformBrowser(this.platformId)
    ) {
      return;
    }

    this.taxExemptReminderCheckInFlight = true;
    try {
      const uid = this.authService.uid();
      const status = await this.userService.getTaxExemptDocumentStatus(uid);
      this.taxExemptReminderChecked = true;
      if (!status.hasDocument) {
        this.notificationService.showTaxExemptDocumentReminder();
      }
    } catch (error) {
      if (shouldLog()) {
        console.warn('[app.tax-exempt-reminder] Failed to check document status', error);
      }
      this.scheduleTaxExemptReminderRetry(retryCount);
    } finally {
      this.taxExemptReminderCheckInFlight = false;
    }
  }

  private scheduleTaxExemptReminderRetry(retryCount: number): void {
    const maxRetries = 5;
    if (retryCount >= maxRetries || !this.authService.isAuthenticated()) {
      return;
    }

    this.cancelTaxExemptReminderRetry();
    this.taxExemptReminderRetry = setTimeout(() => {
      this.taxExemptReminderRetry = undefined;
      void this.showTaxExemptReminderIfNeeded(retryCount + 1);
    }, 1000);
  }

  private cancelTaxExemptReminderRetry(): void {
    if (this.taxExemptReminderRetry) {
      clearTimeout(this.taxExemptReminderRetry);
      this.taxExemptReminderRetry = undefined;
    }
  }

  private findCurrentAuctionId(): string {
    let route: ActivatedRoute | null = this.activatedRoute.root;
    while (route) {
      const aid = route.snapshot.paramMap.get('aid');
      if (aid) {
        return aid;
      }
      route = route.firstChild;
    }
    return this.settingsService.model.last_aid || '';
  }

  private async loadBrowser(sid: string) {
    if (shouldLog()) {
      console.log(`[app.loadBrowser] ${sid}`);
    }
    switch (sid) {
      case 'rasmus_auctions_appspot_com':
        return this.loadRasmusResources();
      case 'cal_auctions_appspot_com':
        return this.loadCalAuctionsResources();
      case 'superb_ascent_appspot_com':
        return this.loadSuperbAscentResources();
    }
  }

  private isProductsRoute(currentPath: string): boolean {
    const path = currentPath || this.extractPath(this.router.url || '');
    return path === '/products' || path.startsWith('/products/');
  }

  private getCurrentBrowserPath(): string {
    return this.extractPath(globalThis.location?.pathname || this.router.url || '');
  }

  private extractPath(url: string): string {
    return (url || '').split('?')[0].split('#')[0];
  }

  private updatePodiumVisibility(hidden: boolean): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const existing = document.getElementById(PODIUM_HIDE_STYLE_ID);
    if (!hidden) {
      existing?.remove();
      return;
    }

    if (existing) {
      return;
    }

    const style = document.createElement('style');
    style.id = PODIUM_HIDE_STYLE_ID;
    style.textContent = `
      #podium-website-widget,
      #podium-bubble,
      #podium-prompt {
        display: none !important;
        visibility: hidden !important;
      }
    `;
    document.head.appendChild(style);
  }

  private injectGTMScript(gtmID: GTM) {

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmID}`;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({'gtm.start': new Date().getTime(), event: 'gtm.js'});

    document.head.appendChild(script);
  }

  /**
   * Inject Stape GTM loader with consent handling and gtag shim.
   */
  private injectStapeGtmLoader(queryParam: string) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (document.getElementById(STAPE_SCRIPT_ELEMENT_ID)) {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    if (!window.gtag) {
      window.gtag = (...args: unknown[]) => {
        window.dataLayer.push(args);
      };
    }

    window.gtag('consent', 'update', { analytics_storage: 'granted' });

    const script = document.createElement('script');
    script.async = true;
    script.id = STAPE_SCRIPT_ELEMENT_ID;
    script.src = `${STAPE_SCRIPT_URL}${queryParam}`;

    // Insert at the very beginning of <head>
    document.head.insertBefore(script, document.head.firstChild);
  }

  /**
   * Dynamically injects the Microsoft Clarity tracking script into the document head.
   *
   * This method creates an inline `<script>` element that initializes the Clarity tracking code
   * with the specified project ID.
   */
  private injectClarityScript(projectId: ClarityProject): void {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.text = `(function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${projectId}");`;
    document.head.appendChild(script);
  }

//    // Ensure the dataLayer array exists
// if (!window.dataLayer) {
//   window.dataLayer = [];
// }

// // Push data to the dataLayer
// window.dataLayer.push({
//   'gtm.start': new Date().getTime(),
//   event: 'gtm.js'
// });



// const noscript = document.createElement('noscript');


// noscript.outerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}"
// height="0" width="0" style="display:none;visibility:hidden"></iframe>')`;
// document.head.insertBefore(noscript, noscript);
//   }

// Optionally, inject the noscript iframe for users with JavaScript disabled
// const noscriptContent = (`
// <noscript><iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}"
// height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>`);
//     document.body.insertAdjacentHTML('afterbegin', noscriptContent);
//   };

  private loadCalAuctionsResources() {
    this.injectGTMScript(GTM.CAL_AUCTIONS);
  }

private loadSuperbAscentResources() {
  if (isDevMode()) {
    this.injectClarityScript(ClarityProject.RASMUS);
    if (shouldLog()) {
      console.log(`[app.loadSuperbAscentResources]`);
    }
  }
  // this.ngZone.runOutsideAngular(() => {
  //   async () => {
  //     async import
  //    braze => {

  //       braze.initialize('874aacbc-8fef-4aae-94f1-9a4650111c0f', {
  //         baseUrl: "sdk.iad-07.braze.com",
  //         enableLogging: true,
  //         allowUserSuppliedJavascript: true,
  //       });
  //       braze.openSession();
  //       if (isDevMode()) {
  //         const brazeUser = braze.getUser();
  //         console.log("brazeUser-onload:", brazeUser?.getUserId());
  //       }};
  //     }
  //   }
  // );
}

  private async loadRasmusResources() {
    if (shouldLog()) {
      console.log(`[app.loadRasmusResources]`);
    }

    // Inject Apple Touch Icon
    const appleTouchIcon = document.createElement('link');
    appleTouchIcon.rel = 'apple-touch-icon';
    appleTouchIcon.sizes = '180x180';
    appleTouchIcon.href = '/assets/img/apple-touch-icon.jpg';
    document.head.appendChild(appleTouchIcon);

    this.injectStapeGtmLoader(STAPE_QUERY_PARAM);

    // Inject Facebook Pixel at the beginning of <head>
    const fbScript = document.createElement('script');
    fbScript.text = `!function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n; n.push=n; n.loaded=!0; n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window,document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');

  fbq('init', '169824206699270');
  fbq('track', 'PageView');`;
    document.head.insertBefore(fbScript, document.head.firstChild);
    try {
      const braze = await this.loadBrazeSdk();
      if (braze && !braze.isInitialized?.()) {
        braze.initialize('a1e52229-e99d-4523-b1a7-9f6f2ab78fb8', {
          baseUrl: 'sdk.iad-07.braze.com',
        });
      }
      braze?.openSession?.();
      // Auth usually resolves (from the rehydrated session) long before this
      // deferred load, so its own sync found no SDK — associate the user now.
      this.authService.syncBrazeUser();
    } catch (error) {
      console.error('[app.loadRasmusResources] Failed to load Braze SDK', error);
    }
    // this.injectGTMScript('GTM-5ZP47SW');

    // this.ngZone.runOutsideAngular(() => {
    //   console.log('zone change outside angular');
    //   async () => {
    //     await import('@braze/web-sdk').then(braze => {
    //       braze.initialize('a1e52229-e99d-4523-b1a7-9f6f2ab78fb8', {
    //         baseUrl: "sdk.iad-07.braze.com",
    //         enableLogging: true,
    //         allowUserSuppliedJavascript: true,
    //       });
    //       braze.openSession();

    //       if (isDevMode()) {
    //         const brazeUser = braze.getUser();
    //         console.log("brazeUser-onload:", brazeUser?.getUserId());
    //       }
    //     }
    //     );
    //   }
    // });


    // document.addEventListener('click', (e: any) => {
    //   if (e.target.matches('#auction-registration-button')) {
    //     ga('gtm4.send', 'event', 'button', 'click', 'register for this auction');
    //   }
    // });
    // document.addEventListener('click', (e: any) => {
    //   if (e.target.matches('auction-registration-I-Agree-Submit')) {
    //     ga('gtm4.send', 'event', 'button', 'click', 'registed for this auction');
    //   }
    // });

    // document.addEventListener('click', (e: any) => {
    //   if (e.target.matches('#bid-button-confirm')) {
    //     ga('gtm4.send', 'event', 'button', 'click', 'bidded on this auction');
    //   }
    // });
    /**
     * Podium chat: invisible until used, and its widget.js costs ~255 KiB plus
     * ~200 ms of main thread — so it waits for the first user interaction
     * rather than loading with the rest of the idle batch. No timer fallback:
     * a visitor who never interacts cannot open a chat either.
     */
    this.runOnFirstInteraction(() => {
      if (document.getElementById('podium-widget')) {
        return;
      }
      const tagEl2 = document.createElement('script');
      tagEl2.src =
        'https://connect.podium.com/widget.js#ORG_TOKEN=4e972742-3212-48da-b8b0-b8cadbb4820b';
      tagEl2.id = 'podium-widget';
      tagEl2.setAttribute('data-organization-api-token', '4e972742-3212-48da-b8b0-b8cadbb4820b');
      document.body.appendChild(tagEl2);
    });
    /* eslint-enable */

    this.injectClarityScript(ClarityProject.RASMUS);
  }

  private loadBrazeSdk(): Promise<any> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve(null);
    }

    if ((window as any).braze) {
      return Promise.resolve((window as any).braze);
    }

    if (this.brazePromise) {
      return this.brazePromise;
    }

    this.brazePromise = new Promise((resolve, reject) => {
      const existing = document.getElementById(BRAZE_SCRIPT_ID);
      if (existing) {
        existing.addEventListener('load', () => resolve((window as any).braze), { once: true });
        existing.addEventListener(
          'error',
          (err) => {
            this.brazePromise = null;
            reject(err);
          },
          { once: true },
        );
        return;
      }

      const script = document.createElement('script');
      script.id = BRAZE_SCRIPT_ID;
      script.async = true;
      script.src = BRAZE_SCRIPT_SRC;
      script.onload = () => resolve((window as any).braze);
      script.onerror = (err) => {
        this.brazePromise = null;
        reject(err);
      };
      document.head.appendChild(script);
    });

    return this.brazePromise;
  }

}
